import { defaultProjectionChartOptions } from './constants';
import { tooltipPositioner } from '../../../utils/tooltip';

type ProjectionPoint = {
    x: number;
    y: number;
    low: number;
    high: number;
};

type ProjectionSeries = {
    data: ProjectionPoint[];
    name?: string;
    type?: string;
};

type ProjectionLabels = {
    bottomLabel: string;
    topLabel: string;
    middleLabel: string;
    middleTopLabel: string;
    middleBottomLabel: string;
    yearsText: string;
    yTitle: string;
};

const adaptSeriesForArea = (data: ProjectionSeries[] = [], labels: ProjectionLabels) => {
    const seriesDivided = [
        { data: [] as ProjectionPoint[], name: labels.bottomLabel },
        { data: [] as ProjectionPoint[], name: labels.topLabel },
        { data: [] as ProjectionPoint[], name: labels.middleLabel },
        { data: [] as ProjectionPoint[], name: labels.middleLabel },
        { data: [] as ProjectionPoint[], name: labels.middleTopLabel },
        { data: [] as ProjectionPoint[], name: labels.middleBottomLabel },
        { data: [] as ProjectionPoint[], name: labels.topLabel },
    ];

    if (!data || data.length === 0) {
        return seriesDivided;
    }

    data[0].data.forEach(({ x, low, high }) => {
        const middle = data[2].data.find((item) => item.x === x) as ProjectionPoint | undefined;
        const middleRange = data[1].data.find((item) => item.x === x) as ProjectionPoint | undefined;

        if (!middle || !middleRange) {
            return;
        }

        seriesDivided[0].data.push({
            x, y: low, high: low, low: middleRange.high,
        });
        seriesDivided[1].data.push({
            x, y: high, high, low: middleRange.low,
        });
        seriesDivided[2].data.push({
            x, y: middle.y, high: middle.y, low: middleRange.low,
        });
        seriesDivided[3].data.push({
            x, y: middle.y, high: middle.y, low: middleRange.high,
        });
        seriesDivided[4].data.push({
            x, y: middleRange.high, high: middleRange.high, low: middleRange.high,
        });
        seriesDivided[5].data.push({
            x, y: middleRange.low, high: middleRange.low, low: middleRange.low,
        });
        seriesDivided[6].data.push({
            x, y: high, high, low: middleRange.low,
        });
    });

    return [...seriesDivided].map((item, idx) => ({
        ...item,
        type: [6].includes(idx) ? 'line' : 'arearange',
        data: item.data.map((i) => ({ ...i, y: i.y || i.low })),

    }));
};

export const getBarChartOptions = (
    min: number,
    max: number,
    data: ProjectionSeries[],
    dataExternal: Array<{ data: Array<[number, number]> }> = [],
    height: number,
    yAxisFormat: (value: number) => string,
    tooltipFormat: (context: any) => string,
    labels: ProjectionLabels,
) => {
    const areaData = adaptSeriesForArea(data, labels);
    const series = [...areaData, ...dataExternal];
    const values = data.map((item) => item.data).flat().map(({ x }) => x);
    const valuesY = areaData.map((item) => item.data).flat().map(({ y }) => y);
    const valuesYExternal = dataExternal?.map((item) => item.data).flat().map((item) => item[1]) ?? [];
    const xMin = Math.min(...values);
    const xMax = Math.max(...values);
    const yMin = Math.min(...valuesY);
    const yMax = Math.max(...valuesY);
    const yExternalMin = valuesYExternal.length ? Math.min(...valuesYExternal) : Infinity;
    const yExternalMax = valuesYExternal.length ? Math.max(...valuesYExternal) : -Infinity;
    const tickPositioner = () => {
        const TICKS_MAX_COUNT = 18;
        const positions = values.length ? [new Date(xMin).valueOf()] : [];
        let tick = xMin;
        let minYear = new Date(xMin).getFullYear();

        while (tick < xMax) {
            minYear += 1;
            tick = new Date(xMin).setFullYear(minYear).valueOf();
            positions.push(tick);
        }

        if (positions.length <= TICKS_MAX_COUNT) return positions;

        const delta = Math.ceil(positions.length / TICKS_MAX_COUNT);
        const selected: number[] = [];

        for (let i = 0; i < positions.length; i += delta) {
            selected.push(positions[i]);
        }

        return selected;
    };
    const yAxisMin = min || Math.min(yMin, yExternalMin);
    const yAxisMax = max || Math.max(yMax, yExternalMax);

    function yAxisFormatter(this: { value: number } | undefined) {
        if (!this) return null;
        if (this.value <= min) return yAxisFormat(this.value);
        if (this.value >= max) return yAxisFormat(this.value);

        return yAxisFormat(this.value);
    }
    function tooltipFormatter(this: any) {
        return `${tooltipFormat(this)}`;
    }
    function positioner(this: { chart: any }, labelWidth: number, labelHeight: number, point: any) {
        return tooltipPositioner({
            labelWidth, labelHeight, point, chart: this.chart, yTitle: labels.yTitle,
        });
    }

    return {
        ...defaultProjectionChartOptions,
        chart: {
            ...defaultProjectionChartOptions.chart,
            height,
        },
        tooltip: {
            ...defaultProjectionChartOptions.tooltip,
            formatter: tooltipFormatter,
            positioner,
        },
        xAxis: {
            ...defaultProjectionChartOptions.xAxis,
            tickPositioner,
            labels: {
                ...defaultProjectionChartOptions.xAxis.labels,
                formatter(this: { isFirst?: boolean; value?: number } | undefined) {
                    if (!this) return '';
                    const { isFirst, value } = this;

                    if (isFirst) return `<b>${labels.yearsText}</b>`;
                    const yearOffset = new Date(value ?? xMin).getFullYear() - new Date(xMin).getFullYear();

                    return `${yearOffset >= 0 ? '+' : ''}${yearOffset}`;
                },
            },
        },
        yAxis: {
            ...defaultProjectionChartOptions.yAxis,
            min: yAxisMin,
            max: yAxisMax,
            title: {
                ...defaultProjectionChartOptions.yAxis.title,
                text: labels.yTitle,
            },
            labels: {
                ...(defaultProjectionChartOptions.yAxis as any).labels,
                formatter: yAxisFormatter,
            },
        },
        series,
        lang: {
            sanitize: function (html: string) {
                return html.replace(
                    /\b(href|src)=["']?(javascript|data|vbscript):/gi,
                    '$1="#"'
                );
            }
        }
    };
};
